
import { computed, defineComponent, PropType, ref } from 'vue'
import { ApplicationGetters, IVacancy, LocalizationPath, VacancyGetters } from '@tokyojob/core'
import {
  ButtonFactory,
  CButton,
  CIconFactory,
  CLabel,
  CVacancyImage,
  getLocalization,
  IconPlacement,
  LabelFactory,
  RouterLinkFactory
} from '@tokyojob/frontend-components'
import { getApplicationStore, LocalizationService, VacancyCardText } from '@tokyojob/frontend-core'
import { getProvidedApp, getRouter } from '~/base/utils/provided-getter'

export default defineComponent({
  components: { CLabel, CVacancyImage, CButton },
  props: {
    /**
     * Vacancy object used to populate details of this card
     */
    vacancyMap: {
      type: Object as PropType<IVacancy>,
      required: true
    },
    /**
     * When not selected by parent, use this css style
     */
    unselectedStyle: {
      type: String,
      required: false,
      default: 'theme-blue-light2-white br-10'
    },
    /**
     * When selected by parent, use this css style
     */
    selectedStyle: {
      type: String,
      required: false,
      default: 'theme-blue-dark1-color br-10'
    },
    hasApplied: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const app = getProvidedApp()
    const router = getRouter()
    const localization = getLocalization(app)

    const selected = ref(false)
    const image = VacancyGetters.getThumbnailUrl(props.vacancyMap)
    const title = VacancyGetters.getTitle(props.vacancyMap)
    const wage = [VacancyCardText.getMapFormattedPrioritizedWage(localization, props.vacancyMap)]
    const bonusText = props.vacancyMap.otherSalariesAndBonuses
      ? LocalizationService.t<string>(localization, LocalizationPath.performanceBonus)
      : ''
    const languageLevel = props.vacancyMap.japaneseConversation?.i18n![0].name!
    const category = VacancyGetters.getCategoryString(props.vacancyMap)
    const id = `vacancy-card-small${props.vacancyMap.id}`

    const applicationStore = getApplicationStore()
    const hasApplied = ApplicationGetters.hasApplied(props.vacancyMap.id!, applicationStore.values())
    const disabledOverlayClass = computed(() => (hasApplied ? 'disabled-overlay-div' : ''))
    const cardStyle = computed(() =>
      props.hasApplied ? props.unselectedStyle : selected.value ? props.selectedStyle : props.unselectedStyle
    )

    const selectedLabelClasses = 'text-small theme-blue-dark1-color-white'
    const unselectedLabelClasses = 'text-small theme-shades-light1-white'

    const wageLabelProps = computed(() =>
      LabelFactory.wages(
        wage,
        bonusText,
        hasApplied ? props.unselectedStyle : selected.value ? selectedLabelClasses : unselectedLabelClasses
      )
    )

    const categoryLabelProps = computed(() =>
      LabelFactory.category(
        category,
        hasApplied ? props.unselectedStyle : selected.value ? selectedLabelClasses : unselectedLabelClasses
      )
    )

    const jpLabelProps = computed(() =>
      LabelFactory.jpLevelSmall(
        languageLevel,
        hasApplied ? props.unselectedStyle : selected.value ? selectedLabelClasses : unselectedLabelClasses
      )
    )

    const createButtonProps = () => {
      const baseClasses = 'z-2 row-center-fullwidth line-1 p-10 g-7 align-center theme-blue-dark1-color-white br-10 text-small'
      const selectedClasses =
        'z-2 row-center-fullwidth line-1 p-10 g-7 align-center color-blue-dark1 theme-blue-dark1-white br-10 text-small'
      const disabledClasses = 'z-2  row-center-fullwidth line-1 p-10 g-7 align-center theme-shades-medium1-color br-10 text-small'
      const buttonBase = ButtonFactory.button({
        icon: CIconFactory.Icon({ icon: 'briefcase', placement: IconPlacement.Left, classes: 'text-big' }),
        label: LocalizationService.t<string>(localization, LocalizationPath.viewJobBtn),
        classes: baseClasses,
        link: { router, link: RouterLinkFactory.localizedLink(`/job/${props.vacancyMap.id}`, app.localizationSelector.language) }
      })

      if (hasApplied) return { ...buttonBase, classes: disabledClasses }
      return selected.value ? { ...buttonBase, classes: selectedClasses } : buttonBase
    }

    const buttonProps = computed(createButtonProps)

    function cardClicked() {
      selected.value = true
      emit('cardClicked', props.vacancyMap)
    }

    return {
      // data properties
      id,
      selected,
      image,
      title,
      wage,
      languageLevel,
      disabledOverlayClass,

      // computed
      buttonProps,
      wageLabelProps,
      jpLabelProps,
      categoryLabelProps,
      cardStyle,

      // methods
      cardClicked
    }
  }
})
